import { useState } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../auth/AuthContext';
import Logo from './Logo';
import LanguageToggle from './LanguageToggle';
import ThemeToggle from './ThemeToggle';
import ServerSwitcher from './ServerSwitcher';
import Icon from './Icon';

export default function DashboardLayout({ title, subtitle, crumb, heading = true, children }) {
  const { t } = useTranslation();
  const { user, logout, servers } = useAuth();
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);

  const doLogout = () => {
    logout();
    navigate('/', { replace: true });
  };

  const isAdmin = user?.role === 'admin';

  // Admin'in sunucusu yok — "İzleme" bölümü tüm müşterilerin verisini gösterir.
  const links = isAdmin
    ? [
        { to: '/dashboard', label: t('adminMon.nav_overview'), icon: 'grid', end: true },
        { to: '/dashboard/alerts', label: t('adminMon.nav_alerts'), icon: 'alert-triangle', end: false },
        { to: '/admin/agents', label: t('adminMon.nav_agents'), icon: 'activity', end: false },
        { to: '/admin/growth', label: t('growth.title'), icon: 'trending-up', end: false },
      ]
    : [
        { to: '/dashboard', label: t('dashboard.title'), icon: 'grid', end: true },
        { to: '/dashboard/alerts', label: t('dashboard.recent_alerts'), icon: 'alert-triangle', end: false },
        { to: '/dashboard/servers', label: t('servers.title'), icon: 'activity', end: false },
        { to: '/dashboard/growth', label: t('growth.title'), icon: 'trending-up', end: false },
        { to: '/dashboard/notifications', label: t('notifications.title'), icon: 'bell', end: false },
        { to: '/dashboard/license', label: t('license.title'), icon: 'key', end: false },
        { to: '/dashboard/referral', label: t('referral.title'), icon: 'gift', end: false },
        { to: '/onboarding', label: t('onboarding.title'), icon: 'download', end: false },
      ];
  const linksLabel = isAdmin ? t('adminMon.group') : t('dashboard.title');

  const adminLinks = isAdmin ? [
    { to: '/admin', label: t('admin.nav_overview'), icon: 'users', end: true },
    { to: '/admin/publishers', label: t('adminPublishers.nav'), icon: 'video', end: false },
    { to: '/admin/coupons', label: t('admin.nav_coupons'), icon: 'ticket', end: false },
    { to: '/admin/settings', label: t('adminSettings.nav'), icon: 'gauge', end: false },
  ] : [];

  const initial = (user?.displayName || user?.email || '?').trim().charAt(0).toUpperCase();

  return (
    <div className="app-shell">
      {menuOpen && <div className="scrim" onClick={() => setMenuOpen(false)} />}
      <aside className={`sidebar ${menuOpen ? 'open' : ''}`}>
        <Logo />
        <div className="nav-group-label">{linksLabel}</div>
        <nav onClick={() => setMenuOpen(false)}>
          {links.map((l) => (
            <NavLink key={l.to} to={l.to} end={l.end} className={({ isActive }) => `side-link ${isActive ? 'active' : ''}`}>
              <Icon name={l.icon} />
              {l.label}
            </NavLink>
          ))}
        </nav>
        {adminLinks.length > 0 && (
          <>
            <div className="nav-group-label">{t('admin.title')}</div>
            <nav onClick={() => setMenuOpen(false)}>
              {adminLinks.map((l) => (
                <NavLink key={l.to} to={l.to} end={l.end} className={({ isActive }) => `side-link ${isActive ? 'active' : ''}`}>
                  <Icon name={l.icon} />
                  {l.label}
                </NavLink>
              ))}
            </nav>
          </>
        )}
        <div className="side-foot">
          <button className="side-link" style={{ width: '100%', background: 'transparent', border: 0, cursor: 'pointer' }} onClick={doLogout}>
            <Icon name="log-out" />
            {t('nav.logout')}
          </button>
        </div>
      </aside>

      <div className="app-main">
        <div className="app-topbar">
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <button className="menu-btn" onClick={() => setMenuOpen((v) => !v)} aria-label="menu">
              <Icon name="menu" size={18} />
            </button>
            <span className="crumb">
              {crumb ? <>{crumb} / <strong>{title}</strong></> : <strong>{title}</strong>}
            </span>
          </div>
          <div className="topbar-right">
            {!isAdmin && servers && servers.length > 0 && <ServerSwitcher />}
            <ThemeToggle />
            <LanguageToggle />
            <span className="avatar" title={user?.email}>{initial}</span>
            <button className="btn btn-ghost btn-sm" onClick={doLogout} title={t('nav.logout')}>
              <Icon name="log-out" size={14} />
              <span className="logout-label">{t('nav.logout')}</span>
            </button>
          </div>
        </div>

        <div className="app-body page-enter">
          {heading && title && <h1 className="page-title">{title}</h1>}
          {heading && subtitle && <p className="page-sub">{subtitle}</p>}
          {children}
        </div>
      </div>
    </div>
  );
}
